/**
 * Volume data loading.
 *
 * Volumes live as static JSON under `public/data/volumes/`, one file per year
 * plus an `index.json` listing the years and their paths. Nothing is bundled:
 * the index is fetched once on first use, and a year's articles are fetched
 * only when someone actually opens that volume.
 *
 *   index.json  ->  { journal, issn, volumes: [{ year, path, articleCount, … }] }
 *   2026.json   ->  { year, articles: [ … ] }
 *
 * Every request is memoised as a promise, so concurrent callers (the header and
 * the page body both want the index on first paint) share a single fetch, and
 * revisiting a volume costs nothing.
 */

const INDEX_PATH = '/data/volumes/index.json';

/** Respect CRA's PUBLIC_URL so the app still works when served from a sub-path. */
function withBase(pathname) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}${pathname}`;
}

async function fetchJson(pathname) {
  const response = await fetch(withBase(pathname), {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Could not load ${pathname} (HTTP ${response.status})`);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Index
// ---------------------------------------------------------------------------

let indexPromise = null;

/**
 * The list of volumes: year, path, article count, page range, last publication
 * date. Small enough that the header can load it on every page.
 *
 * @returns {Promise<{journal: string, issn: string, volumes: Array}>}
 */
export function loadVolumeIndex() {
  if (!indexPromise) {
    indexPromise = fetchJson(INDEX_PATH)
      .then((data) => ({
        ...data,
        volumes: [...(data.volumes || [])].sort(
          (a, b) => Number(b.year) - Number(a.year)
        ),
      }))
      // Don't cache a failure — a transient network error shouldn't leave the
      // nav permanently empty for the rest of the session.
      .catch((error) => {
        indexPromise = null;
        throw error;
      });
  }
  return indexPromise;
}

/** Just the years, newest first — what the header dropdown needs. */
export async function loadVolumeYears() {
  const { volumes } = await loadVolumeIndex();
  return volumes.map((volume) => volume.year);
}

// ---------------------------------------------------------------------------
// A single volume
// ---------------------------------------------------------------------------

const volumeCache = new Map();

/**
 * Articles for one year. Looks the year up in the index so the path comes from
 * the data rather than from a hardcoded filename pattern, and falls back to the
 * conventional path if the index has not been refreshed yet.
 *
 * @param {string|number} year
 * @returns {Promise<Array>} the volume's articles, ordered by first page
 */
export function loadVolume(year) {
  const key = String(year);
  if (!volumeCache.has(key)) {
    const promise = loadVolumeIndex()
      .then(({ volumes }) => {
        const entry = volumes.find((volume) => String(volume.year) === key);
        return fetchJson(entry ? entry.path : `/data/volumes/${key}.json`);
      })
      .then((data) => data.articles || [])
      .catch((error) => {
        volumeCache.delete(key);
        throw error;
      });
    volumeCache.set(key, promise);
  }
  return volumeCache.get(key);
}

/**
 * One article, by the `documentName` used in its route.
 *
 * @param {string|number} year
 * @param {string} documentName
 * @returns {Promise<Object|null>}
 */
export async function loadArticle(year, documentName) {
  const articles = await loadVolume(year);
  return articles.find((article) => article.documentName === documentName) || null;
}
